import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { usersAPI } from '../../api';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchOrganizationProfiles } from '../../ducks/organizations.duck';
import {
  buildSetOrgSubscriptionPatch,
  SUBSCRIPTION_TIERS,
} from '../../util/orgSubscription';
import { v4 as uuidv4 } from 'uuid';

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/NewOrganizationPage/SET_INITIAL_STATE';

export const CREATE_ORGANIZATION_REQUEST = 'app/NewOrganizationPage/CREATE_ORGANIZATION_REQUEST';
export const CREATE_ORGANIZATION_SUCCESS = 'app/NewOrganizationPage/CREATE_ORGANIZATION_SUCCESS';
export const CREATE_ORGANIZATION_ERROR = 'app/NewOrganizationPage/CREATE_ORGANIZATION_ERROR';

// ================ Reducer ================ //

const initialState = {
  createOrganizationInProgress: false,
  createOrganizationError: null,
};

export default function newOrganizationPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };

    case CREATE_ORGANIZATION_REQUEST:
      return {
        ...state,
        createOrganizationInProgress: true,
        createOrganizationError: null,
      };
    case CREATE_ORGANIZATION_SUCCESS:
      return {
        ...state,
        createOrganizationInProgress: false,
        createOrganizationError: null,
      };
    case CREATE_ORGANIZATION_ERROR:
      return {
        ...state,
        createOrganizationInProgress: false,
        createOrganizationError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const createOrganizationRequest = () => ({
  type: CREATE_ORGANIZATION_REQUEST,
});
export const createOrganizationSuccess = () => ({
  type: CREATE_ORGANIZATION_SUCCESS,
});
export const createOrganizationError = error => ({
  type: CREATE_ORGANIZATION_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

export const createOrganization = organizationData => async (dispatch, getState, sdk) => {
  dispatch(createOrganizationRequest());

  try {
    const { currentUser } = getState().user;
    const currentOrgProfiles =
      currentUser.attributes?.profile?.publicData?.organizationProfiles || [];

    // Generate a unique ID for the new organization (SDK doesn't auto-generate for publicData)
    const newOrganizationId = uuidv4();

    // Create new organization profile
    const newOrganization = {
      id: newOrganizationId,
      name: organizationData.name,
      bio: organizationData.bio,
      website: organizationData.website,
      founded: organizationData.founded,
      founder: organizationData.founder,
      instagramLink: organizationData.instagramLink,
      profileImage: organizationData.profileImage,
    };

    // Add to existing organizations
    const updatedOrganizationProfiles = [...currentOrgProfiles, newOrganization];

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    // Update user's publicData with new organization
    const response = await sdk.currentUser.updateProfile(
      {
        publicData: {
          organizationProfiles: updatedOrganizationProfiles,
        },
      },
      queryParams
    );

    // Get the actual organization ID from the response
    const updatedUser = response.data.data;
    const createdOrgProfiles = updatedUser.attributes.profile.publicData.organizationProfiles;

    // Find the newly created organization (it should be the last one we added)
    const createdOrganization = createdOrgProfiles.find(org => org.name === organizationData.name);
    const realOrganizationId = createdOrganization ? createdOrganization.id : newOrganizationId;

    // Write per-org subscription record into privateData (spec shape).
    const currentPrivateData = currentUser.attributes?.profile?.privateData || {};
    const updatedPrivateData = buildSetOrgSubscriptionPatch(
      currentPrivateData,
      realOrganizationId,
      {
        tier: SUBSCRIPTION_TIERS.FREEMIUM,
        status: 'active',
      }
    );

    await usersAPI.updateProfile({
      id: currentUser.id.uuid,
      privateData: updatedPrivateData,
    });

    dispatch(addMarketplaceEntities(response));
    dispatch(createOrganizationSuccess());

    // Fetch fresh user data to reflect changes
    dispatch(fetchCurrentUser());
    dispatch(fetchOrganizationProfiles());

    return {
      organizationId: realOrganizationId,
      organizationName: organizationData.name,
    };
  } catch (e) {
    dispatch(createOrganizationError(storableError(e)));
    throw e;
  }
};
